import { useState, useEffect } from "react";

const API_URL =
  "https://api.cricapi.com/v1/currentMatches?apikey=bd5bb499-e994-4cb7-9c6f-fe27a78d24fe";

function getStatus(match) {
  if (!match.matchStarted) return "upcoming";
  if (match.matchStarted && !match.matchEnded) return "live";
  return "done";
}

function getSeriesName(match) {
  const parts = (match.name || "").split(",");
  return parts.length >= 3 ? parts.slice(2).join(",").trim() : "Other";
}

function Badge({ status }) {
  const styles = {
    live: {
      background: "rgba(226,75,74,0.12)",
      color: "#a32d2d",
      border: "0.5px solid rgba(226,75,74,0.35)",
    },
    done: {
      background: "rgba(29,158,117,0.1)",
      color: "#0f6e56",
      border: "0.5px solid rgba(29,158,117,0.3)",
    },
    upcoming: {
      background: "rgba(55,138,221,0.1)",
      color: "#185fa5",
      border: "0.5px solid rgba(55,138,221,0.3)",
    },
  };
  const labels = { live: "● Live", done: "Done", upcoming: "Upcoming" };
  return (
    <span
      style={{
        ...styles[status],
        fontSize: 10,
        fontWeight: 500,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {labels[status]}
    </span>
  );
}

function TeamRow({ team, scores }) {
  const sc = scores?.find(
    (s) =>
      s.inning
        .toLowerCase()
        .includes((team.name || "").toLowerCase().split(" ")[0]) ||
      s.inning.toLowerCase().includes((team.shortname || "").toLowerCase())
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={team.img || ""}
          alt={team.shortname}
          onError={(e) => (e.target.style.opacity = "0.2")}
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            objectFit: "contain",
            border: "0.5px solid var(--color-border-tertiary)",
            background: "var(--color-background-secondary)",
          }}
        />
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--color-text-primary)",
          }}
        >
          {team.shortname || team.name}
        </span>
      </div>
      <div>
        {sc ? (
          <>
            <span
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: "var(--color-text-primary)",
              }}
            >
              {sc.r}/{sc.w}
            </span>
            <span
              style={{
                fontSize: 11,
                color: "var(--color-text-secondary)",
                marginLeft: 3,
              }}
            >
              ({sc.o})
            </span>
          </>
        ) : (
          <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
            —
          </span>
        )}
      </div>
    </div>
  );
}

function MatchCard({ match }) {
  const status = getStatus(match);
  const teams = match.teamInfo || [];
  const scores = match.score || [];

  return (
    <div
      style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: "var(--border-radius-lg)",
        padding: "1rem 1.25rem",
        transition: "border-color 0.15s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.borderColor = "var(--color-border-secondary)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.borderColor = "var(--color-border-tertiary)")
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: "var(--color-text-secondary)",
            flex: 1,
            lineHeight: 1.4,
          }}
        >
          {match.name || ""}
        </span>
        <Badge status={status} />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: "var(--color-text-secondary)",
          marginBottom: 8,
        }}
      >
        <span>{match.venue || ""}</span>
        <span>{match.date || ""}</span>
      </div>

      <div
        style={{
          height: "0.5px",
          background: "var(--color-border-tertiary)",
          margin: "8px 0",
        }}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {teams.length > 0 ? (
          teams.map((team, i) => (
            <TeamRow key={i} team={team} scores={scores} />
          ))
        ) : (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            No team info
          </span>
        )}
      </div>

      <div
        style={{
          height: "0.5px",
          background: "var(--color-border-tertiary)",
          margin: "8px 0",
        }}
      />

      <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
        {match.status || ""}
      </div>
    </div>
  );
}

function StatPill({ color, label, count }) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
    >
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <span style={{ color: "var(--color-text-secondary)" }}>{label}</span>
      &nbsp;
      <span style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>
        {count}
      </span>
    </div>
  );
}

export default function CricketLive() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [lastUpdated, setLastUpdated] = useState("");

  useEffect(() => {
    fetch(API_URL)
      .then((r) => r.json())
      .then((res) => {
        setMatches(res.data || []);
        const now = new Date();
        setLastUpdated(
          `Updated ${now.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        );
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const seriesList = [
    "All",
    ...Array.from(new Set(matches.map(getSeriesName))),
  ];

  const filtered =
    activeFilter === "All"
      ? matches
      : matches.filter((m) => getSeriesName(m) === activeFilter);

  const live = matches.filter((m) => getStatus(m) === "live").length;
  const upcoming = matches.filter((m) => getStatus(m) === "upcoming").length;
  const done = matches.filter((m) => getStatus(m) === "done").length;

  return (
    <div
      style={{
        fontFamily: "var(--font-sans, sans-serif)",
        padding: "0 0 2rem",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          padding: "1rem 0 0.75rem",
          borderBottom: "0.5px solid var(--color-border-tertiary)",
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
          Cricket Live
        </h1>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {lastUpdated}
        </span>
      </div>

      {/* Stats row */}
      {!loading && !error && (
        <div
          style={{
            display: "flex",
            gap: 16,
            padding: "10px 0",
            flexWrap: "wrap",
            borderBottom: "0.5px solid var(--color-border-tertiary)",
          }}
        >
          <StatPill color="#e24b4a" label="Live" count={live} />
          <StatPill color="#378add" label="Upcoming" count={upcoming} />
          <StatPill color="#1d9e75" label="Completed" count={done} />
          <StatPill
            color="var(--color-text-secondary)"
            label="Total"
            count={matches.length}
          />
        </div>
      )}

      {/* Filters */}
      {!loading && !error && (
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "10px 0",
            flexWrap: "wrap",
            borderBottom: "0.5px solid var(--color-border-tertiary)",
          }}
        >
          {seriesList.map((s) => (
            <button
              key={s}
              onClick={() => setActiveFilter(s)}
              style={{
                background:
                  activeFilter === s
                    ? "var(--color-text-primary)"
                    : "transparent",
                color:
                  activeFilter === s
                    ? "var(--color-background-primary)"
                    : "var(--color-text-secondary)",
                border:
                  activeFilter === s
                    ? "0.5px solid var(--color-text-primary)"
                    : "0.5px solid var(--color-border-secondary)",
                padding: "4px 14px",
                borderRadius: 999,
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: activeFilter === s ? 500 : 400,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading && (
        <div
          style={{
            padding: "3rem 0",
            textAlign: "center",
            color: "var(--color-text-secondary)",
            fontSize: 14,
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              border: "1.5px solid var(--color-border-secondary)",
              borderTopColor: "var(--color-text-primary)",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Fetching live scores...
        </div>
      )}

      {error && (
        <div
          style={{
            color: "var(--color-text-danger)",
            fontSize: 13,
            padding: "1rem 0",
          }}
        >
          Failed to load scores: {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div
          style={{
            color: "var(--color-text-secondary)",
            fontSize: 13,
            padding: "2rem 0",
            textAlign: "center",
          }}
        >
          No matches found
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
            paddingTop: 12,
          }}
        >
          {filtered.map((match) => (
            <MatchCard key={match.id} match={match} />
          ))}
        </div>
      )}
    </div>
  );
}
